import type { MobileFilterDrawerChip } from '@/components/dashboard/mobile/MobileFilterDrawer';
import type { AssetType } from '@/lib/types/property';
import { ASSET_TYPE_LABELS, formatAssetTypeLabel } from '@/lib/constants/propertyOptions';
import { formatPublicCurrency } from '@/lib/utils/publicCurrency';
import { formatSquareFeet } from '@/lib/utils/propertyFormatting';

export interface AvailabilityFiltersState {
  monthlyRent: { min: number | null; max: number | null };
  areaSf: { min: number | null; max: number | null };
  permittedUses: string[];
}

export interface RangeBounds {
  min: number;
  max: number;
}

export interface AvailabilityFilterChipActions {
  removePermittedUse: (value: string) => void;
  clearMonthlyRentRange: () => void;
  clearAreaRange: () => void;
}

export interface AvailabilityFilterChipLabels {
  priceLabel: string;
  sizeLabel: string;
  useLabel: string;
}

export type AvailabilityFiltersSetter = (
  next:
    | AvailabilityFiltersState
    | ((current: AvailabilityFiltersState) => AvailabilityFiltersState),
) => void;

export function createEmptyAvailabilityFilters(): AvailabilityFiltersState {
  return {
    monthlyRent: { min: null, max: null },
    areaSf: { min: null, max: null },
    permittedUses: [],
  };
}

export function normalizeRange(
  min: number | null,
  max: number | null,
): [number | null, number | null] {
  if (min !== null && max !== null && min > max) {
    return [max, min];
  }
  return [min, max];
}

export function getRangeError(min: number | null, max: number | null): string | null {
  if (min !== null && max !== null && min > max) {
    return 'Min cannot exceed max.';
  }
  return null;
}

export function parseAvailabilityFilters(raw: string | null): AvailabilityFiltersState | null {
  if (typeof raw !== 'string' || raw.trim().length === 0) {
    return null;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<AvailabilityFiltersState> & {
      rentPerSf?: Partial<AvailabilityFiltersState['monthlyRent']>;
    };
    const monthlyRent: Partial<AvailabilityFiltersState['monthlyRent']> = parsed.monthlyRent ?? {};
    const areaSf: Partial<AvailabilityFiltersState['areaSf']> = parsed.areaSf ?? {};
    const permittedUses = Array.isArray(parsed.permittedUses)
      ? parsed.permittedUses.filter((entry): entry is string => typeof entry === 'string')
      : [];

    return {
      monthlyRent: {
        min: typeof monthlyRent.min === 'number' ? monthlyRent.min : null,
        max: typeof monthlyRent.max === 'number' ? monthlyRent.max : null,
      },
      areaSf: {
        min: typeof areaSf.min === 'number' ? areaSf.min : null,
        max: typeof areaSf.max === 'number' ? areaSf.max : null,
      },
      permittedUses,
    };
  } catch {
    return null;
  }
}

export function countAvailabilityActiveFilters(filters: AvailabilityFiltersState): number {
  let count = 0;
  if (filters.monthlyRent.min !== null || filters.monthlyRent.max !== null) {
    count += 1;
  }
  if (filters.areaSf.min !== null || filters.areaSf.max !== null) {
    count += 1;
  }
  if (filters.permittedUses.length > 0) {
    count += 1;
  }
  return count;
}

export function filtersEqualAvailabilityFilters(
  a: AvailabilityFiltersState,
  b: AvailabilityFiltersState,
): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function buildAvailabilityFilterSummaryChips(
  filters: AvailabilityFiltersState,
  actions: AvailabilityFilterChipActions,
  labels?: AvailabilityFilterChipLabels,
): MobileFilterDrawerChip[] {
  const chips: MobileFilterDrawerChip[] = [];
  const resolveLabel = (value: string | undefined, fallback: string) =>
    typeof value === 'string' && value.trim().length > 0 ? value : fallback;
  const priceLabel = resolveLabel(labels?.priceLabel, 'Monthly rent');
  const sizeLabel = resolveLabel(labels?.sizeLabel, 'Size');
  const useLabel = resolveLabel(labels?.useLabel, 'Use');

  const [rentMin, rentMax] = normalizeRange(filters.monthlyRent.min, filters.monthlyRent.max);
  const rentLabel = formatRangeLabel(rentMin, rentMax, (value) => `${formatMonthlyRent(value)}/mo`);
  if (rentLabel) {
    chips.push({
      id: 'rent-range',
      label: `${priceLabel} ${rentLabel}`,
      onRemove: actions.clearMonthlyRentRange,
    });
  }

  const [areaMin, areaMax] = normalizeRange(filters.areaSf.min, filters.areaSf.max);
  const areaLabel = formatRangeLabel(areaMin, areaMax, (value) => `${formatSquareFeet(value)} sf`);
  if (areaLabel) {
    chips.push({
      id: 'area-range',
      label: `${sizeLabel} ${areaLabel}`,
      onRemove: actions.clearAreaRange,
    });
  }

  filters.permittedUses.forEach((value) => {
    chips.push({
      id: `use-${value}`,
      label: `${useLabel} ${formatPermittedUseLabel(value)}`,
      onRemove: () => {
        actions.removePermittedUse(value);
      },
    });
  });

  return chips;
}

export function createAvailabilityFilterChipActions(
  setFilters: AvailabilityFiltersSetter,
): AvailabilityFilterChipActions {
  return {
    removePermittedUse: (value) => {
      setFilters((current) => ({
        ...current,
        permittedUses: current.permittedUses.filter((entry) => entry !== value),
      }));
    },
    clearMonthlyRentRange: () => {
      setFilters((current) => ({ ...current, monthlyRent: { min: null, max: null } }));
    },
    clearAreaRange: () => {
      setFilters((current) => ({ ...current, areaSf: { min: null, max: null } }));
    },
  };
}

export function formatLabel(value: string): string {
  return value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatPermittedUseLabel(value: string): string {
  if (value in ASSET_TYPE_LABELS) {
    return formatAssetTypeLabel(value as AssetType);
  }
  return formatLabel(value);
}

function formatRangeLabel(
  min: number | null,
  max: number | null,
  formatValue: (value: number) => string,
): string {
  if (min === null && max === null) {
    return '';
  }

  if (min !== null && max !== null) {
    const formattedMin = formatValue(min);
    const formattedMax = formatValue(max);
    return min === max ? formattedMin : `${formattedMin} - ${formattedMax}`;
  }

  if (min !== null) {
    return `${formatValue(min)}+`;
  }

  return `<= ${formatValue(max ?? 0)}`;
}

function formatMonthlyRent(value: number): string {
  return formatPublicCurrency(value);
}
